import type { ReactElement } from 'react';
import { DefinitionList, type Definition } from '../data/index.js';
import type { CatalogModel, CatalogModelDetail } from '../../lib/api/queries/catalog.js';

/**
 * The catalogued model behind a unit — P-Q005/P-Q006.
 *
 * A unit carries only `model_id`; everything a reader recognises as "what this
 * machine is" comes from here. `modelLine` is exported because the search card,
 * the detail header and this component must all name a model the same way.
 */
export function modelLine(model: CatalogModel | undefined): string | null {
  if (model === undefined) return null;
  const line = [model.manufacturer, model.model_name, model.model_variant]
    .filter((part): part is string => typeof part === 'string' && part !== '')
    .join(' ');
  return line === '' ? null : line;
}

export interface ModelSummaryProps {
  model: CatalogModelDetail | undefined;
  /** From the unit, not the model — this machine's own build year. */
  yearOfManufacture: number | null;
  /**
   * How the catalogued specification is presented.
   *
   * `rows` is the definition list every owner-side surface uses. `grid` lays
   * the same values out as tiles, which is what the customer's detail page
   * needs — a specification is the thing a hirer scans for, and a column of
   * hairline rows is the shape of a record rather than of a product.
   *
   * It is a LAYOUT switch and nothing else: the same specifications, the same
   * values, the same note beneath them. Rendering the specs twice on one page
   * so that one copy could look different would be two answers about a machine.
   */
  specificationLayout?: 'rows' | 'grid';
}

export function ModelSummary({
  model,
  yearOfManufacture,
  specificationLayout = 'rows',
}: ModelSummaryProps): ReactElement {
  const items: Definition[] = [];
  const line = modelLine(model);
  if (line !== null) items.push({ term: 'Model', value: line });
  if (yearOfManufacture !== null) {
    items.push({ term: 'Year of manufacture', value: yearOfManufacture, numeric: true });
  }
  if (
    model !== undefined &&
    model.operator_requirement !== null &&
    model.operator_requirement !== ''
  ) {
    items.push({ term: 'Operator', value: model.operator_requirement.replace(/_/g, ' ') });
  }
  if (model !== undefined && model.default_meter_type !== null && model.default_meter_type !== '') {
    items.push({
      term: 'Metered by',
      value: `${model.default_meter_type.replace(/_/g, ' ')}${
        model.default_meter_unit_code === null ? '' : ` (${model.default_meter_unit_code})`
      }`,
    });
  }

  const specifications = (model?.specifications ?? []).filter(
    (spec) =>
      spec.value_text !== null ||
      spec.value_numeric !== null ||
      spec.value_boolean !== null ||
      spec.value_enum_code !== null,
  );

  return (
    <>
      <DefinitionList items={items} label="Machine identity" />
      {specifications.length === 0 ? null : (
        <>
          <h3 className="subsection-title">Catalogued specification</h3>
          {specificationLayout === 'grid' ? (
            <ul className="spec-grid stagger">
              {specifications.map((spec) => (
                <li key={spec.attribute_code} className="spec-tile">
                  <p className="spec-tile__term">{spec.attribute_code.replace(/_/g, ' ')}</p>
                  <p className={`spec-tile__value${spec.value_numeric !== null ? ' numeric' : ''}`}>
                    {`${
                      spec.value_text ??
                      spec.value_enum_code?.replace(/_/g, ' ') ??
                      spec.value_numeric ??
                      (spec.value_boolean === true ? 'Yes' : 'No')
                    }${spec.unit_code === null ? '' : ` ${spec.unit_code}`}`}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <DefinitionList
              items={specifications.map((spec) => ({
                term: spec.attribute_code.replace(/_/g, ' '),
                numeric: spec.value_numeric !== null,
                value: `${
                  spec.value_text ??
                  spec.value_enum_code?.replace(/_/g, ' ') ??
                  spec.value_numeric ??
                  (spec.value_boolean === true ? 'Yes' : 'No')
                }${spec.unit_code === null ? '' : ` ${spec.unit_code}`}`,
              }))}
            />
          )}
          <p className="discovery-note">
            These values describe the catalogued model, not this individual machine’s current
            condition.
          </p>
        </>
      )}
    </>
  );
}
